/**
 * Build category → group → item hierarchy for import review UI.
 * When temporary_id / parent_temporary_id are present, also expose a recursive forest.
 */

import {
  formatSpecificationDisplay,
  parseSpecificationLines,
  specificationsToTextarea,
} from './specifications';
import {
  buildCategoryForest,
  type HierarchyNode,
} from './modificationHierarchy';

export interface ReviewItemRef {
  index: number;
}

export interface ReviewGroupNode {
  key: string;
  label: string;
  items: ReviewItemRef[];
}

export interface ReviewCategoryNode {
  category: string;
  groups: ReviewGroupNode[];
  itemCount: number;
  /** Recursive roots when import items carry temporary_id links. */
  roots?: HierarchyNode<ParsedModLike>[];
  recursive?: boolean;
}

export interface ParsedModLike {
  category?: string;
  group_label?: string;
  parentComponent?: string;
  temporary_id?: string | null;
  parent_temporary_id?: string | null;
  parent_modification_id?: string | number | null;
  title?: string;
  item_type?: string;
  is_group?: boolean;
  specifications?: string[] | Record<string, unknown> | string;
  notes?: string;
}

const UNGROUPED_LABEL = 'General';

export function getGroupLabel(mod: ParsedModLike): string {
  const label = (mod.group_label || mod.parentComponent || '').trim();
  return label || UNGROUPED_LABEL;
}

export function groupKey(category: string, groupLabel: string): string {
  return `${category}::${groupLabel}`;
}

function hasTempHierarchy(mods: ParsedModLike[]): boolean {
  return mods.some((m) => Boolean(m.temporary_id) || Boolean(m.parent_temporary_id));
}

/** Preserve first-seen order of categories and groups. */
export function buildReviewHierarchy(mods: ParsedModLike[]): ReviewCategoryNode[] {
  if (hasTempHierarchy(mods)) {
    return buildCategoryForest(mods).map((cat) => {
      // Legacy groups: flatten roots for callers that still read groups.
      const groups: ReviewGroupNode[] = [
        {
          key: groupKey(cat.category, 'Tree'),
          label: 'Tree',
          items: cat.roots.map((r) => ({ index: r.index })),
        },
      ];
      return {
        category: cat.category,
        groups,
        itemCount: cat.itemCount,
        roots: cat.roots,
        recursive: true,
      };
    });
  }

  const categoryOrder: string[] = [];
  const categoryMap = new Map<string, Map<string, ReviewItemRef[]>>();

  mods.forEach((mod, index) => {
    const category = (mod.category || 'Miscellaneous').trim() || 'Miscellaneous';
    const groupLabel = getGroupLabel(mod);

    if (!categoryMap.has(category)) {
      categoryMap.set(category, new Map());
      categoryOrder.push(category);
    }

    const groups = categoryMap.get(category)!;
    if (!groups.has(groupLabel)) {
      groups.set(groupLabel, []);
    }
    groups.get(groupLabel)!.push({ index });
  });

  return categoryOrder.map((category) => {
    const groups = categoryMap.get(category)!;
    const groupNodes: ReviewGroupNode[] = [];

    groups.forEach((items, label) => {
      groupNodes.push({
        key: groupKey(category, label),
        label,
        items,
      });
    });

    const itemCount = groupNodes.reduce((sum, g) => sum + g.items.length, 0);

    return { category, groups: groupNodes, itemCount, recursive: false };
  });
}

export function formatSpecifications(specs: ParsedModLike['specifications']): string {
  return specificationsToTextarea(specs);
}

export function parseSpecificationsInput(value: string): string[] {
  return parseSpecificationLines(value);
}

/** Bullet lines for display (same as formatSpecificationDisplay). */
export function specificationDisplayLines(specs: ParsedModLike['specifications']): string[] {
  return formatSpecificationDisplay(specs);
}

/** Notes shown in review — hide auto-generated source prefix in display hint only. */
export function displayNotes(notes?: string): string {
  if (!notes) return '';
  return notes.trim();
}

export function countDistinctGroups(mods: ParsedModLike[]): number {
  const labels = new Set(
    mods.map((m) => {
      const g = getGroupLabel(m);
      return g === UNGROUPED_LABEL ? null : g;
    }).filter(Boolean)
  );
  return labels.size;
}
